import { Component } from 'preact';
import { ChevronLeft } from 'lucide-react';
import { GOLD, ACCENT_GOLD, TEXT_PRIMARY, TEXT_MUTED, TEXT_BODY } from '../../lib/medievalTheme';
import { resolveAvatarUrl } from '../../utils/arena/avatarUtils';
import AvatarImage from '../ui/AvatarImage';
import { formatRank } from '../../utils/arena/rankUtils';
import { xpProgressInLevel } from '../../utils/arena/profileDefaults';
import { getPublicProfile } from '../../utils/friendsApi';
import { playUI, UI } from '../../utils/arena/uiSounds';
import PlayerTitle from '../PlayerTitle';
import PlayerBadgeStrip from '../PlayerBadgeStrip';
import { t } from '../../localization/t';
import { isFreePlay } from '../../utils/freePlay';

// Phone player profile. Full-screen overlay showing another player's
// public card: portrait, name + title, rank, and the battle record.
// Mobile-appropriate scope on purpose: the desktop FriendProfileOverlay
// also carries the treasury, collection breakdown, match history,
// achievements, published decks and the card showcase, none of which fit
// a landscape phone. Read-only; no edit affordances, no friend actions.

// Same palette the desktop ladder uses, duplicated the way
// MobileLeaderboard duplicates it (the map is tiny, tier names stable).
const TIER_HEX = {
  apprentice: '#a8a29e',
  journeyman: '#d4d4d8',
  adept: '#38bdf8',
  expert: '#a78bfa',
  master: '#fbbf24',
  grandmaster: '#f87171',
};

// Carved-tablet chip. The mobile cousin of the desktop profile's
// STONE_TABLET_STYLE, sized in rems and without the texture blend
// (phone GPUs get nothing from a blend layer at this size).
const TABLET = {
  background: 'linear-gradient(180deg, rgba(38,30,18,0.92) 0%, rgba(22,16,9,0.94) 55%, rgba(14,10,6,0.96) 100%)',
  border: `1px solid ${GOLD} 0.45)`,
  borderRadius: 'var(--radius-lg)',
  boxShadow: 'inset 0 1px 0 rgba(255,230,170,0.10), inset 0 -2px 6px rgba(0,0,0,0.55), 0 4px 14px rgba(0,0,0,0.5)',
};

const CHIP = {
  background: 'rgba(20,14,6,0.95)',
  border: `1px solid ${GOLD} 0.4)`,
  borderRadius: 'var(--radius-md)',
  padding: '0.15rem 0.5rem',
};

export default class MobileProfile extends Component {
  constructor(props) {
    super(props);
    // Viewing yourself paints instantly from the profile the hub
    // already holds, then refreshes from the server underneath.
    const seed = props.selfProfile && props.selfProfile.id === props.playerId ? props.selfProfile : null;
    this.state = { profile: seed, loading: !seed, error: null };
  }

  componentDidMount() {
    this.load();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.playerId !== this.props.playerId) {
      this.setState({ profile: null, loading: true, error: null });
      this.load();
    }
  }

  componentWillUnmount() {
    this._unmounted = true;
  }

  load = async () => {
    const playerId = this.props.playerId;
    try {
      const profile = await getPublicProfile(playerId);
      if (this._unmounted || this.props.playerId !== playerId) return;
      this.setState({ profile, loading: false, error: null });
    } catch {
      if (this._unmounted || this.props.playerId !== playerId) return;
      // Keep whatever we seeded rather than blanking a usable screen.
      this.setState((s) => ({
        loading: false,
        error: s.profile ? null : t('The archives did not answer.'),
      }));
    }
  };

  handleBack = () => {
    playUI(UI.CANCEL);
    this.props.onClose?.();
  };

  handleRetry = () => {
    playUI(UI.CLICK);
    this.setState({ loading: true, error: null });
    this.load();
  };

  renderStat(label, value, color) {
    return (
      <div
        key={label}
        className="flex flex-col items-center justify-center text-center"
        style={{ ...TABLET, minHeight: '3.25rem' }}
      >
        <div className="font-bold tabular-nums text-lg leading-none" style={{ color, textShadow: '0 2px 4px rgba(0,0,0,0.6)' }}>
          {value}
        </div>
        <div className="mt-1 text-xs" style={{ color: TEXT_MUTED }}>{label}</div>
      </div>
    );
  }

  renderProfile(profile) {
    const { sorceryCards } = this.props;
    const avatarUrl = resolveAvatarUrl(profile, sorceryCards);
    const level = xpProgressInLevel(profile.xp || 0).level;
    const wins = profile.wins || 0;
    const losses = profile.losses || 0;
    const total = wins + losses;
    const winRate = total > 0 ? Math.round((wins / total) * 100) : 0;
    const tierHex = TIER_HEX[profile.rank?.tier] || TEXT_MUTED;
    const isOnline = !!profile.online;
    const statusText = profile.customStatus || (isOnline ? t('Online') : t('Offline'));

    return (
      <div className="flex gap-4 px-4 py-3">
        {/* Identity column */}
        <div className="shrink-0 flex flex-col items-center text-center" style={{ width: '10rem' }}>
          <div className="relative">
            {avatarUrl ? (
              <AvatarImage
                src={avatarUrl}
                style={{
                  width: '5.5rem',
                  height: '5.5rem',
                  borderRadius: 'var(--radius-lg)',
                  border: `1px solid ${ACCENT_GOLD}`,
                  boxShadow: `0 0 0 2px rgba(20,14,6,0.95), 0 0 18px ${GOLD} 0.35)`,
                }}
              />
            ) : (
              <div
                className="flex items-center justify-center font-bold arena-heading"
                style={{
                  width: '5.5rem',
                  height: '5.5rem',
                  fontSize: '2rem',
                  borderRadius: 'var(--radius-lg)',
                  background: 'radial-gradient(circle at 40% 35%, rgba(60,44,18,0.95) 0%, rgba(20,14,6,0.95) 70%)',
                  border: `1px solid ${ACCENT_GOLD}`,
                  color: ACCENT_GOLD,
                  boxShadow: `0 0 0 2px rgba(20,14,6,0.95), 0 0 18px ${GOLD} 0.35)`,
                }}
              >
                {(profile.name || '?')[0].toUpperCase()}
              </div>
            )}
            <div
              className="absolute rounded-full"
              style={{
                width: '0.85rem',
                height: '0.85rem',
                right: '-0.2rem',
                bottom: '-0.2rem',
                background: isOnline ? '#6ab04c' : 'rgba(120,110,100,0.55)',
                border: '2px solid rgba(15,12,6,0.98)',
                boxShadow: isOnline ? '0 0 8px rgba(106,176,76,0.6)' : 'none',
              }}
            />
          </div>

          <div
            className="arena-heading font-bold mt-2 w-full truncate text-lg"
            style={{
              color: ACCENT_GOLD,
              letterSpacing: '0.01em',
              textShadow: '0 2px 4px rgba(0,0,0,0.8)',
            }}
          >
            {profile.name}
          </div>
          {/* The block variant hardcodes a cqw font size on its inner
              node, so we use the inline one: its class list and style
              prop both reach the rendered element, letting the rem size
              below win over the cqw class. */}
          <PlayerTitle
            player={profile}
            sorceryCards={sorceryCards}
            prefix=""
            className="w-full"
            style={{ fontSize: '0.75rem', display: 'block' }}
          />
          <PlayerBadgeStrip
            player={profile}
            sorceryCards={sorceryCards}
            size="1.75rem"
            className="mt-2 justify-center"
          />
        </div>

        {/* Record column */}
        <div className="flex-1 min-w-0 flex flex-col gap-2">
          <div className="flex items-center flex-wrap gap-1.5">
            {isFreePlay() ? null : <span className="text-xs font-semibold" style={{ ...CHIP, color: ACCENT_GOLD }}>{t('Lv {level}', { level })}</span>}
            <span className="text-xs font-semibold" style={{ ...CHIP, color: tierHex }}>
              {formatRank(profile.rank?.tier, profile.rank?.division)}
            </span>
            <span
              className="inline-flex items-center gap-1.5 text-xs"
              style={{
                ...CHIP,
                border: `1px solid ${isOnline ? 'rgba(106,176,76,0.4)' : `${GOLD} 0.25)`}`,
                color: isOnline ? TEXT_BODY : TEXT_MUTED,
              }}
            >
              <span
                className="rounded-full"
                style={{
                  width: '0.4rem',
                  height: '0.4rem',
                  background: isOnline ? '#6ab04c' : 'rgba(120,110,100,0.55)',
                }}
              />
              {statusText}
            </span>
          </div>

          {typeof profile.glicko?.rating === 'number' ? (
            <div className="flex items-center justify-between px-3" style={{ ...TABLET, minHeight: '2.75rem' }}>
              <div className="flex flex-col leading-tight">
                <span className="text-xs" style={{ color: TEXT_MUTED }}>{t('Skill Rating')}</span>
                <span className="text-xs" style={{ color: `${GOLD} 0.55)`, letterSpacing: '0.05em' }}>Glicko-2</span>
              </div>
              <div className="font-bold tabular-nums text-xl" style={{ color: ACCENT_GOLD }}>
                {Math.round(profile.glicko.rating)}
                {typeof profile.glicko.rd === 'number' ? (
                  <span className="tabular-nums text-xs font-medium ml-1.5" style={{ color: TEXT_MUTED }}>
                    ±{Math.round(profile.glicko.rd)}
                  </span>
                ) : null}
              </div>
            </div>
          ) : null}

          <div className="grid grid-cols-4 gap-2">
            {this.renderStat(t('Matches'), total, TEXT_PRIMARY)}
            {this.renderStat(t('Win Rate'), `${winRate}%`, ACCENT_GOLD)}
            {this.renderStat(t('Wins'), wins, '#7ad06a')}
            {this.renderStat(t('Losses'), losses, '#e07060')}
          </div>

          {!isFreePlay() && typeof profile.uniqueCards === 'number' ? (
            <div className="flex items-center justify-between px-3 text-xs" style={{ ...TABLET, minHeight: '2.25rem' }}>
              <span style={{ color: TEXT_MUTED }}>{t('Collection')}</span>
              <span className="tabular-nums font-semibold" style={{ color: TEXT_PRIMARY }}>
                {typeof profile.collectionSize === 'number'
                  ? t('{unique} unique · {cards} cards', { unique: Number(profile.uniqueCards).toLocaleString(), cards: Number(profile.collectionSize).toLocaleString() })
                  : t('{unique} unique', { unique: Number(profile.uniqueCards).toLocaleString() })}
              </span>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  render() {
    const { profile, loading, error } = this.state;

    return (
      <div className="absolute inset-0 z-30 flex flex-col phone-safe-pad" style={{ background: 'rgba(8,6,4,0.97)' }}>
        {/* Header */}
        <div className="flex items-center gap-2 px-2 pt-2 pb-1.5" style={{ borderBottom: `1px solid ${GOLD} 0.18)` }}>
          <button
            type="button"
            aria-label={t('Back')}
            className="flex items-center justify-center shrink-0 active:scale-[0.92] transition-transform duration-100"
            style={{ width: '2.75rem', height: '2.75rem' }}
            onClick={this.handleBack}
          >
            <ChevronLeft size={24} color={TEXT_PRIMARY} />
          </button>
          <span className="arena-heading text-lg font-bold min-w-0 truncate" style={{ color: TEXT_PRIMARY }}>
            {profile?.name || t('Profile')}
          </span>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto" style={{ overscrollBehavior: 'contain' }}>
          {loading ? (
            <div className="flex items-center justify-center h-full text-sm" style={{ color: TEXT_MUTED }}>
              {t('Consulting the archives…')}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 px-6 text-center">
              <div className="text-sm" style={{ color: TEXT_MUTED }}>{error}</div>
              <button
                type="button"
                className="arena-heading font-semibold px-6 transition-transform duration-100 active:translate-y-px active:brightness-110"
                style={{
                  ...TABLET,
                  minHeight: '2.75rem',
                  color: ACCENT_GOLD,
                  letterSpacing: '0.03em',
                }}
                onClick={this.handleRetry}
              >
                {t('Try Again')}
              </button>
            </div>
          ) : profile ? (
            this.renderProfile(profile)
          ) : null}
        </div>
      </div>
    );
  }
}
